import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Briefcase, Loader2, DollarSign, MapPin, AlertCircle } from 'lucide-react';
import { useWorkforceListings, useMyApplications, useApplyToListing, useWithdrawApplication } from '@/hooks/useWorkforceApi';
import { useVisibilitySettings } from '@/hooks/useConnectApi';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const ROLE_LABELS: Record<string, string> = {
  student: 'Student / Intern',
  technician: 'Technician',
  advisor: 'Advisor',
  manager: 'Manager',
  instructor: 'Instructor',
};

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  submitted: 'secondary',
  reviewing: 'default',
  accepted: 'default',
  rejected: 'destructive',
  withdrawn: 'outline',
};

export function WorkforceOpportunities() {
  const { data: visibility } = useVisibilitySettings();
  const isVisible = visibility?.visible_to_shops === true;
  // Core-100 R4 (OD-15): the directory is served to people who are discoverable on Connect.
  const { data: listings, isLoading } = useWorkforceListings(isVisible);
  const { data: myApps } = useMyApplications();
  const applyMutation = useApplyToListing();
  const withdrawMutation = useWithdrawApplication();

  const getApp = (listingId: string) => myApps?.find(a => a.listing_id === listingId) ?? null;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Workforce Opportunities</CardTitle>
        </div>
        <CardDescription>
          Internships, apprenticeships, and open positions from shops in your area.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!isVisible ? (
          <p className="text-sm text-muted-foreground py-4 text-center" data-testid="workforce-not-discoverable">
            Turn on visibility to see and apply for opportunities.
          </p>
        ) : !listings?.length ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No open opportunities right now. Check back later!
          </p>
        ) : (
          <div className="space-y-3">
            {listings.map((listing) => {
              const app = getApp(listing.id);
              const appStatus = app?.status ?? null;
              // Core-100 R4 W2 (CORE-Q): an open application can be withdrawn by the person; that closes the shop's read.
              const canWithdraw = !!app && (appStatus === 'submitted' || appStatus === 'reviewing');
              const shopName = listing.shop_name;

              return (
                <div key={listing.id} className="rounded-lg border p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-1">
                      <h3 className="font-medium text-sm">{listing.title}</h3>
                      <p className="text-xs text-muted-foreground">{shopName}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Badge variant="outline" className="text-xs">
                        {ROLE_LABELS[listing.role_target] ?? listing.role_target}
                      </Badge>
                      {listing.paid && (
                        <Badge variant="secondary" className="text-xs gap-0.5">
                          <DollarSign className="h-3 w-3" /> Paid
                        </Badge>
                      )}
                    </div>
                  </div>

                  {listing.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2">{listing.description}</p>
                  )}

                  <div className="flex items-center justify-between pt-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {listing.location_zip && (
                        <span className="flex items-center gap-0.5">
                          <MapPin className="h-3 w-3" />
                          {listing.location_zip}
                        </span>
                      )}
                    </div>

                    {appStatus ? (
                      <div className="flex items-center gap-2">
                        <Badge variant={STATUS_VARIANT[appStatus] ?? 'outline'} className="text-xs capitalize">
                          {appStatus}
                        </Badge>
                        {canWithdraw && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs"
                            data-testid="withdraw-application"
                            disabled={withdrawMutation.isPending}
                            onClick={() => withdrawMutation.mutate(app!.id)}
                          >
                            Withdraw
                          </Button>
                        )}
                      </div>
                    ) : !isVisible ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button size="sm" variant="outline" disabled className="text-xs gap-1">
                                <AlertCircle className="h-3 w-3" /> Apply
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Turn on visibility to apply for opportunities.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <Button
                        size="sm"
                        variant="default"
                        className="text-xs"
                        disabled={applyMutation.isPending}
                        onClick={() => applyMutation.mutate(listing.id)}
                      >
                        {applyMutation.isPending ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : 'Apply'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
